import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Users, Award, Zap, Wifi, Building2, Radio, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Formations = () => {
  const { t } = useLanguage();

  const formations = [
    {
      id: 'aipr',
      icon: Radio,
      title: t('formations.aiprTitle'),
      duration: t('formations.aiprDuration'),
      desc: t('formations.aiprDesc'),
      levels: [t('formations.aiprLevel1'), t('formations.aiprLevel2'), t('formations.aiprLevel3')],
      details: [t('formations.aiprDetail1'), t('formations.aiprDetail2')]
    },
    {
      id: 'elec',
      icon: Zap,
      title: t('formations.elecTitle'),
      duration: t('formations.elecDuration'),
      desc: t('formations.elecDesc'),
      levels: [t('formations.elecLevel1'), t('formations.elecLevel2'), t('formations.elecLevel3'), t('formations.elecLevel4')],
      details: [t('formations.elecDetail1'), t('formations.elecDetail2'), t('formations.elecDetail3'), t('formations.elecDetail4')]
    },
    {
      id: 'telecom',
      icon: Wifi,
      title: t('formations.telecomTitle'),
      duration: t('formations.telecomDuration'),
      desc: t('formations.telecomDesc'),
      levels: [],
      details: [t('formations.telecomDetail1'), t('formations.telecomDetail2'), t('formations.telecomDetail3'), t('formations.telecomDetail4'), t('formations.telecomDetail5'), t('formations.telecomDetail6')]
    },
    {
      id: 'domotique',
      icon: Building2,
      title: t('formations.domotiqueTitle'),
      duration: t('formations.domotiqueDuration'),
      desc: t('formations.domotiqueDesc'),
      levels: [],
      details: [t('formations.domotiqueDetail1'), t('formations.domotiqueDetail2'), t('formations.domotiqueDetail3'), t('formations.domotiqueDetail4'), t('formations.domotiqueDetail5'), t('formations.domotiqueDetail6')]
    }
  ];

  return (
    <div data-testid="formations-page">
      <section className="page-header page-header-professionnels" data-testid="formations-header">
        <p className="text-sm uppercase tracking-widest text-[#C9A227] mb-4 font-sans animate-fade-in">{t('formations.tagline')}</p>
        <h1 className="font-serif text-4xl md:text-6xl text-white mb-6 animate-fade-in-delay-1">{t('formations.heroTitle')}</h1>
        <div className="gold-line gold-line-center mb-6 animate-fade-in-delay-2"></div>
        <p className="text-white/80 text-lg max-w-2xl text-center animate-fade-in-delay-3">{t('formations.heroSubtitle')}</p>
      </section>

      <div className="container-luxury pt-8">
        <Link to="/professionnels" className="inline-flex items-center gap-2 text-[#C9A227] hover:text-[#b08d1f] transition-colors text-sm" data-testid="formations-back">
          <ArrowLeft className="w-4 h-4" /> {t('formations.backToPro')}
        </Link>
      </div>

      <section className="section-luxury bg-[#FDFCF8] texture-overlay" data-testid="formations-list">
        <div className="container-luxury">
          <div className="space-y-12">
            {formations.map((f) => (
              <div key={f.id} className="bg-white border border-[#F5EFD3] hover:border-[#C9A227] transition-all duration-300" data-testid={`formation-${f.id}`}>
                <div className="p-8 md:p-10">
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    <div className="w-16 h-16 rounded-full bg-[#C9A227] flex items-center justify-center flex-shrink-0">
                      <f.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                        <h2 className="font-serif text-2xl md:text-3xl text-[#1A1A1A]">{f.title}</h2>
                        <span className="inline-flex items-center gap-1.5 text-sm text-[#C9A227] bg-[#C9A227]/10 px-3 py-1 border border-[#C9A227]/20 w-fit">
                          <Clock className="w-3.5 h-3.5" /> {f.duration}
                        </span>
                      </div>
                      <p className="text-gray-600 text-lg leading-relaxed mb-6">{f.desc}</p>

                      {f.levels.length > 0 && (
                        <div className="mb-6">
                          <h3 className="text-sm uppercase tracking-widest text-[#C9A227] mb-3 font-sans">{t('formations.levels')}</h3>
                          <div className="flex flex-wrap gap-2">
                            {f.levels.map((level, i) => (
                              <span key={i} className="text-sm bg-[#1A1A1A] text-white px-3 py-1.5">{level}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      <h3 className="text-sm uppercase tracking-widest text-[#C9A227] mb-3 font-sans">{t('formations.program')}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {f.details.map((detail, i) => (
                          <div key={i} className="flex items-start gap-2 p-3 bg-[#FDFCF8] border border-[#F5EFD3]">
                            <Award className="w-4 h-4 text-[#C9A227] flex-shrink-0 mt-0.5" />
                            <span className="text-gray-600 text-sm">{detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-luxury bg-[#1A1A1A]" data-testid="formations-cta">
        <div className="container-luxury text-center max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">{t('formations.ctaTitle')}</h2>
          <div className="gold-line gold-line-center mb-8"></div>
          <p className="text-white/70 text-lg mb-10">{t('formations.ctaText')}</p>
          <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="formations-contact-cta">
            {t('formations.contactUs')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Formations;
